import React, { useEffect, useState } from 'react';
import { Table, TextInput, rem, createStyles, Text, Center, Flex, ScrollArea, Button, Skeleton, Title, Box, Pagination } from '@mantine/core';
import { IconSearch, IconDownload, IconCheck } from '@tabler/icons-react';
import { keys } from '@mantine/utils';
import { getBetaUsers, getAllBetaUsers } from '../../services/api.service';
import { notifications } from '@mantine/notifications';
import { saveAs } from 'file-saver';
import Papa from 'papaparse';

// Custom styles
const useStyles = createStyles((theme) => ({
	container: {
		padding: theme.spacing.md,
		height: '100%',
	},
	th: {
		padding: '0 !important',
	},
	control: {
		width: '100%',
		padding: `${theme.spacing.xs} ${theme.spacing.md}`,
		'&:hover': {
			backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[1],
		},
	},
	icon: {
		width: rem(21),
		height: rem(21),
		borderRadius: rem(21),
	},
}));

const BetaUsers = () => {

	// State and hooks
	const [isLoading, setIsLoading] = useState(true);
	const [isCsvLoading, setCsvLoading] = useState(false);
	const [search, setSearch] = useState('');
	const [betaUsers, setBetaUsers] = useState([]);
	const [filteredData, setFilteredData] = useState([]);
	const { classes } = useStyles();
	const [total, setTotal] = useState(0);
	const [limit] = useState(10);
	const [page, setPage] = useState(1);
	const offset = page - 1;

	// On load
	useEffect(() => {
		getBetaUsersList();
		// eslint-disable-next-line
	}, []);

	// Get beta users
	const getBetaUsersList = () => {

		// Get
		getBetaUsers(`?limit=${limit}&offset=${offset}`, function (response) {

			// Set beta users and initial sorted data, remove loader
			setBetaUsers(response.data.data);
			setFilteredData(response.data.data);
			setTotal(response.data.total);
			setIsLoading(false);

		}, function (error) {

			// Remove loader
			setIsLoading(false);

			// Show error
			notifications.show({
				title: 'Error',
				message: error,
				color: 'red'
			});

		});

	}

	// TH component
	const Th = ({ children, width }) => {
		const { classes } = useStyles();
		return (
			<th className={classes.th} width={width}>
				<Text
					fw={700}
					fz="sm"
					className={classes.control}
				>
					{children}
				</Text>
			</th>
		);
	}

	// Build table head
	const ths = (
		<tr>
			<Th>
				ID
			</Th>
			<Th>
				Nombre
			</Th>
			<Th>
				Descripción
			</Th>
			<Th>
				Correo Electrónico
			</Th>
		</tr>
	);

	// Build rows
	const rows = filteredData.map((element) => (
		<tr key={element.id}>
			<td width={100}>
				{element.id}
			</td>
			<td>
				{element.name}
			</td>
			<td>
				{element.description}
			</td>
			<td>
				{element.email}
			</td>
		</tr>
	));

	// Handle search change
	const handleSearchChange = (event) => {
		const { value } = event.currentTarget;
		setSearch(value);
		filterData(betaUsers, value);
	};

	// Filter and return from query/search/sort
	const filterData = (data, search) => {
		const query = search.toLowerCase().trim();
		setFilteredData(data.filter((item) => keys(data[0]).some((key) => item[key].toString().toLowerCase().includes(query))));
	}

	// Convert and download as CSV file
	const convertToCSV = () => {

		// Set loader
		setCsvLoading(true);

		// Get complete list of users
		getAllBetaUsers(function (response) {

			// Assign
			const allUsers = response.data;

			// Parse and convert to CSV
			const csv = Papa.unparse(allUsers, { header: true });

			// Generate file
			const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });

			// Save file
			saveAs(blob, 'beta_users.csv');

			// Show success
			notifications.show({
				title: 'Archivo descargado correctamente',
				color: 'green',
				icon: <IconCheck />
			});

			// Remove loader
			setCsvLoading(false);

		}, function (error) {

			// Remove loader
			setCsvLoading(false);

			// Show error
			notifications.show({
				title: 'Error',
				message: error,
				color: 'red'
			});

		});

	};

	// Listen to page changes
	useEffect(() => {

		// Get information (if not initial)
		if (!isLoading) getBetaUsersList();

		// eslint-disable-next-line
	}, [page]);

	return (
		<Box className="mainContainer">
			{/* Content */}
			<Flex
				direction="column"
				justify="space-between"
				className={classes.container}
			>
				{/* Title */}
				<Title mb="xl">
					Usuarios Beta
				</Title>
				{/* Group */}
				<Flex direction="column">
					{/* Top */}
					<Flex mb="md">
						{/* New Level */}
						<Button
							onClick={convertToCSV}
							variant="filled"
							leftIcon={<IconDownload size={18} />}
							loading={isCsvLoading}
						>
							Descargar CSV
						</Button>
						{/* Search */}
						<TextInput
							placeholder="Buscar usuarios"
							icon={<IconSearch size="0.9rem" stroke={1.5} />}
							value={search}
							onChange={handleSearchChange}
							ml="sm"
							sx={{
								width: '100%',
								maxWidth: 350
							}}
							variant="filled"
						/>
					</Flex>
					{/* Table */}
					<ScrollArea>
						{isLoading ?
							<>
								<Skeleton height={40} radius="sm" />
								<Skeleton height={32} mt={12} radius="sm" />
								<Skeleton height={32} mt={12} radius="sm" />
								<Skeleton height={32} mt={12} radius="sm" />
								<Skeleton height={32} mt={12} radius="sm" />
							</>
							:
							<Table
								highlightOnHover
								sx={{ flex: 2 }}
							>
								<thead>{ths}</thead>
								<tbody>{rows.length > 0 ? (
									rows
								) : (
									<tr>
										<td colSpan={3}>
											<Text
												weight={500}
												align="center"
												my="xl"
												color="dimmed"
											>
												Sin resultados
											</Text>
										</td>
									</tr>
								)}</tbody>
							</Table>
						}
					</ScrollArea>
				</Flex>
				{/* Pages */}
				<Center sx={{ marginTop: 20 }}>
					<Pagination
						total={Math.ceil(total / limit)}
						siblings={1}
						defaultValue={1}
						withEdges
						onChange={setPage}
						value={page}
					/>
				</Center>
			</Flex>
		</Box>
	);

}

export default BetaUsers;
